/**
 * Modal — shared accessible dialog. TASK-028 P1-5 (PR-4).
 *
 * Matches the in-repo reference ARIA (DeadlineCalendarExportModal /
 * ExportReviewModal): role="dialog" + aria-modal + aria-labelledby + a labelled
 * close button. ADDS the focus management those references lack and that axe
 * cannot verify:
 *   - initial focus moves into the dialog on open
 *   - Tab / Shift+Tab are trapped inside the dialog (can't reach the page behind)
 *   - Escape, the close button, and backdrop click all close
 *   - focus returns to the element that opened the dialog (focus is restored
 *     BEFORE React unmounts the dialog — the PR-3 useMenuButton technique — so
 *     the unmount can't bounce focus to <body>)
 *
 * Mouse behavior preserved: backdrop click-to-close (toggle with
 * closeOnBackdropClick). Error announcements / live regions are intentionally
 * NOT here — that's PR-5 (P1-7/P1-8).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  type ButtonHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { X } from 'lucide-react';

/**
 * Lets children close the dialog through the SAME focus-restoring path as the X /
 * Escape / backdrop (focus the opener before unmount). Footer "Done"/"Cancel"
 * buttons that call the parent's onClose directly would bypass it and fall back
 * to the cleanup-on-unmount path. Use `<ModalCloseButton>` or `useModalClose()`.
 */
interface ModalContextValue {
  close: () => void;
}
const ModalContext = createContext<ModalContextValue | null>(null);

export function useModalClose(): () => void {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error('useModalClose must be used within a <Modal>');
  return ctx.close;
}

/** A close button that routes through the Modal's focus-restoring close path. */
export function ModalCloseButton({
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>): JSX.Element {
  const close = useModalClose();
  return (
    <button type="button" {...props} onClick={close}>
      {children}
    </button>
  );
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const SIZE_CLASS: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
};

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Visible dialog title; also the accessible name via aria-labelledby. */
  title: string;
  children: ReactNode;
  size?: ModalSize;
  /** Optional element to focus first instead of the first focusable in the dialog. */
  initialFocusRef?: RefObject<HTMLElement>;
  /** Allow mouse users to dismiss by clicking the backdrop (default true). */
  closeOnBackdropClick?: boolean;
  /** When true, the close affordances are disabled (e.g. a submit is in flight). */
  closeDisabled?: boolean;
  /** Optional header content rendered under the title (e.g. a subtitle). */
  headerSlot?: ReactNode;
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'lg',
  initialFocusRef,
  closeOnBackdropClick = true,
  closeDisabled = false,
  headerSlot,
}: ModalProps): JSX.Element | null {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const titleId = useId();

  const requestClose = useCallback(() => {
    if (closeDisabled) return;
    // Restore focus to the opener BEFORE the dialog unmounts so the unmount
    // can't bounce focus to <body>.
    previousFocus.current?.focus();
    onClose();
  }, [closeDisabled, onClose]);

  // On open: remember the opener, move focus inside. On close/unmount: restore
  // focus to the opener (backstop for programmatic closes that bypass requestClose).
  useEffect(() => {
    if (!isOpen) return;
    previousFocus.current = (document.activeElement as HTMLElement) ?? null;
    const initial =
      initialFocusRef?.current ??
      dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE) ??
      dialogRef.current;
    initial?.focus();
    const opener = previousFocus.current;
    return () => opener?.focus();
  }, [isOpen, initialFocusRef]);

  const ctxValue = useMemo(() => ({ close: requestClose }), [requestClose]);

  if (!isOpen) return null;

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      requestClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const focusables = Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [],
    );
    if (focusables.length === 0) {
      e.preventDefault();
      dialogRef.current?.focus();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (!dialogRef.current?.contains(active as Node)) {
      // Focus drifted outside the dialog — pull it back in.
      e.preventDefault();
      first.focus();
    } else if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <ModalContext.Provider value={ctxValue}>
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/40"
      role="presentation"
      onClick={(e) => {
        if (closeOnBackdropClick && e.target === e.currentTarget) requestClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className={`bg-white rounded-lg shadow-xl w-full ${SIZE_CLASS[size]} max-h-[90vh] flex flex-col outline-none`}
      >
        <div className="flex items-start justify-between px-5 py-4 border-b border-soft-neutral">
          <div className="min-w-0">
            <h2 id={titleId} className="text-lg font-semibold text-ink">
              {title}
            </h2>
            {headerSlot}
          </div>
          <button
            type="button"
            onClick={requestClose}
            disabled={closeDisabled}
            aria-label="Close"
            // P2-12: 44px min touch target (hit area only — icon size unchanged).
            className="ml-1 flex-shrink-0 inline-flex items-center justify-center min-w-[44px] min-h-[44px] rounded-full hover:bg-soft-neutral text-muted transition-colors disabled:opacity-50"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
      </div>
    </div>
    </ModalContext.Provider>
  );
}

export default Modal;
